import { Component, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { forLifeOf, ignoreNullish, LifecycleAware } from '@lsq-frontend/common-ui';
import { I18n } from '@ngx-translate/i18n-polyfill';
import { CreditSummaryDto } from '@lsq-frontend/remote-services/all-java/financing-mk2-services/model/credit-summary-dto';
import { MoneyDto } from '@lsq-frontend/remote-services/all-java/financing-mk2-services/model/money-dto';
import type { ChartOptions } from 'chart.js';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { LsqDataSource } from '@lsq-frontend/angular-shared/api-service';

@LifecycleAware()
@Component({
    selector: 'account-credit-summary-tile',
    styleUrls: ['./credit-summary-tile.component.scss'],
    encapsulation: ViewEncapsulation.None,
    template: `
        <card class="credit-summary-card">
            <div card-header>
                <h3 id="credit-summary-card-heading" i18n>Credit</h3>
            </div>
            <div class="credit-summary-card-body" card-body>
                <lsq-circle-loader *ngIf="dataSource.loading$ | async"></lsq-circle-loader>
                <div class="credit-summary-card-body-inner" *ngIf="!(dataSource.loading$ | async)">
                    <ng-container *ngIf="creditDataIsZero$ | async">
                        <div class="credit-summary-chart credit-summary-chart-no-data">
                            <div class="chart-center">
                                There is no credit data to display.
                            </div>
                            <lsq-chart
                                class="doughnut-chart"
                                [options]="chartOptions"
                                [colors]="[blankChartColor]"
                                [labels]="['No Data']"
                                [data]="[100]"
                            >
                            </lsq-chart>
                        </div>
                    </ng-container>

                    <ng-container *ngIf="!(creditDataIsZero$ | async)">
                        <div class="credit-summary-chart">
                            <lsq-chart
                                class="doughnut-chart"
                                [options]="chartOptions"
                                [colors]="chartColors"
                                [labels]="chartLabels"
                                [data]="chartData$ | async | notNull"
                            >
                            </lsq-chart>
                        </div>
                    </ng-container>

                    <div class="chart-legend">
                        <ng-container
                            *ngFor="let moneyAmount of chartLegendData$ | async; let i = index"
                        >
                            <div class="chart-legend-section">
                                <div class="chart-legend-label">
                                    {{ chartLabels[i] }}
                                </div>
                                <div class="chart-legend-value">
                                    {{ moneyAmount | lsqCurrency }}
                                </div>
                            </div>
                        </ng-container>
                    </div>
                </div>
            </div>
        </card>
    `
})
export class AccountCreditSummaryTileComponent implements OnInit, OnDestroy {
    @Input() public dataSource!: LsqDataSource<CreditSummaryDto>;
    public creditDataIsZero$!: Observable<boolean>;
    public chartData$!: Observable<number[]>;
    public chartLegendData$!: Observable<MoneyDto[]>;

    public readonly chartOptions: ChartOptions = {
        cutoutPercentage: 66,
        aspectRatio: 1,
        responsive: true,
        maintainAspectRatio: true,
        tooltips: {
            enabled: false
        },
        rotation: 1 * Math.PI,
        circumference: 1 * Math.PI
    };
    public readonly chartColors: string[] = ['#008661', '#80949E'];
    public readonly chartLabels: string[] = [
        this._i18n('Credit Limit'),
        this._i18n('Credit Utilized'),
        this._i18n('Credit Remaining')
    ];
    public readonly blankChartColor = '#eef0f7';

    constructor(private _i18n: I18n) {}

    public ngOnInit(): void {
        this.creditDataIsZero$ = this.dataSource.data$.pipe(
            map(
                (creditSummaryDto) =>
                    creditSummaryDto.creditLimit?.cents === 0 &&
                    creditSummaryDto.creditUsed?.cents === 0 &&
                    creditSummaryDto.creditRemaining?.cents === 0
            )
        );
        this.chartData$ = this.dataSource.data$.pipe(
            forLifeOf(this),
            ignoreNullish(),
            map((data) => [data.creditRemaining!.cents!, data.creditUsed!.cents!])
        );
        this.chartLegendData$ = this.dataSource.data$.pipe(
            forLifeOf(this),
            ignoreNullish(),
            map((data) => [data.creditLimit!!, data.creditUsed!!, data.creditRemaining!!])
        );
    }

    public ngOnDestroy(): void {}
}
